'use client';

import { FC, useMemo } from 'react';

import { Breadcrumbs as RadianceBreadcrumbs } from '@ravago/shared/radiance/components/breadcrumbs/components/Breadcrumbs/Breadcrumbs';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { useTranslations } from '@ravago/shared/translations/client';

import { Link } from '../link/link';

export type BreadcrumbsPath = { route: string; name: string }[];

interface Props {
   color: 'primary' | 'light';
   locale: string;
   path: BreadcrumbsPath;
   currentPathName: string;
   isConsumer: boolean;
}

export const Breadcrumbs: FC<Props> = ({ color, locale, path, currentPathName, isConsumer }) => {
   const translate = useTranslations();

   const backButton = useMemo(() => {
      if (!path || path.length === 0) return `/${locale}`;
      return `/${locale}/${path[path.length - 1].route}`;
   }, [path, locale]);

   return (
      <>
         <BreakpointSwitch useContainerQuery={!isConsumer} includeViews={['desktop']}>
            <RadianceBreadcrumbs theme={color === 'primary' ? 'light' : 'dark'}>
               <Link href={`/${locale}`} color={color} inactive={!isConsumer}>
                  <Icon icon="house" color={color === 'primary' ? 'strong' : 'inherit'} size="body-highlight" />
               </Link>
               {path.length === 0
                  ? null
                  : path.map(({ route, name }) => (
                       <Link
                          href={`/${locale}/${route}`}
                          key={route}
                          color={color === 'primary' ? 'dark' : 'light'}
                          inactive={!isConsumer}
                          underline="none"
                       >
                          {name}
                       </Link>
                    ))}
               {!isConsumer && (
                  <Link href="/" color={color} inactive>
                     ...
                  </Link>
               )}
               <Link href="/" color={color} inactive>
                  {currentPathName}
               </Link>
            </RadianceBreadcrumbs>
         </BreakpointSwitch>
         <BreakpointSwitch useContainerQuery={!isConsumer} excludeViews={['desktop']}>
            <Link href={backButton} color={color === 'primary' ? 'dark' : 'light'} variant="body-highlight" underline="none" inactive={!isConsumer}>
               <Icon icon="chevron-left" />
               {path.length === 0 ? translate('page-consumer.breadcrumb-home') : path[path.length - 1]?.name || translate('back')}
            </Link>
         </BreakpointSwitch>
      </>
   );
};
